import {
  KioskHomeButtonDto,
  KioskSettingsDto,
  KioskStringsDto,
  KnownKioskHardwareConfigurationDtoCameraDirection,
  KnownKioskHardwareConfigurationDtoCameraDriver,
  KnownKioskHardwareConfigurationDtoCameraRotation,
  KnownKioskHardwareConfigurationDtoScannerMode
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { KioskSettingsForm, KioskStringValues } from './form';

const INTERNAL_USER_ACTION = 'internal_session';
const EXTERNAL_USER_ACTION = 'external_session';
const BUTTON_DEFAULT_STYLE = 'primary';
const LABEL_LOCALE = 'default';
const DEFAULT_COUNTRY_CODE = 'AU';

const STRING_KEYS: (keyof KioskStringsDto)[] = [
  'internalUserSingular',
  'internalUserPlural',
  'externalUserSingular',
  'externalUserPlural',
  'scanQrCodeHeading',
  'scanQrCodeSubheading',
  'externalLoginPhoneLoginHeading',
  'externalLoginPhoneLoginSubheading',
  'collectionCodeSubHeading',
  'noDoorsAvailableSubheading',
  'csPhoneInternal',
  'csPhoneExternal',
  'groundfloorPortalAddress'
];

/**
 * Collapse whitespace runs (incl. NBSP and \r\n vs \n line endings) and trim, so strings that look
 * identical on screen compare equal even when the stored and default copies differ invisibly.
 */
function normaliseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function matchesDefault(value: string, defaultValue: string | undefined): boolean {
  if (!defaultValue) return false;
  return normaliseWhitespace(value) === normaliseWhitespace(defaultValue);
}

/** A value equal to its kiosk-wide default is treated as not customised. */
function customisedString(value: string | undefined, defaultValue: string | undefined): string {
  if (!value || matchesDefault(value, defaultValue)) return '';
  return value;
}

function mapStrings<V>(
  keys: (keyof KioskStringsDto)[],
  getValue: (key: keyof KioskStringsDto) => V
): Record<keyof KioskStringsDto, V> {
  const result = {} as Record<keyof KioskStringsDto, V>;
  for (const key of keys) {
    result[key] = getValue(key);
  }
  return result;
}

export class KioskSettingsFormOutput implements KioskStringValues {
  name: string;
  mode: string;
  showInternalUserButton: 'yes' | 'no';
  internalUserButtonText: string;
  showExternalUserButton: 'yes' | 'no';
  externalUserButtonText: string;
  externalFlow: string;
  scannerMode: string;
  cameraEnabled: boolean;
  cameraDriver: string;
  cameraDirection: string;
  cameraRotation: string;
  lockerType: string;
  countryCode: string;
  internalUserSingular: string;
  internalUserPlural: string;
  externalUserSingular: string;
  externalUserPlural: string;
  scanQrCodeHeading: string;
  scanQrCodeSubheading: string;
  externalLoginPhoneLoginHeading: string;
  externalLoginPhoneLoginSubheading: string;
  collectionCodeSubHeading: string;
  noDoorsAvailableSubheading: string;
  csPhoneInternal: string;
  csPhoneExternal: string;
  groundfloorPortalAddress: string;

  constructor(form: Partial<KioskSettingsForm>) {
    this.name = form.name ?? '';
    this.mode = form.mode ?? '';
    this.showInternalUserButton = form.showInternalUserButton ?? 'no';
    this.internalUserButtonText = form.internalUserButtonText ?? '';
    this.showExternalUserButton = form.showExternalUserButton ?? 'no';
    this.externalUserButtonText = form.externalUserButtonText ?? '';
    this.externalFlow = form.externalFlow ?? '';
    this.scannerMode = form.scannerMode ?? '';
    this.cameraEnabled = form.cameraEnabled ?? true;
    this.cameraDriver = form.cameraDriver ?? KnownKioskHardwareConfigurationDtoCameraDriver.Default;
    this.cameraDirection = form.cameraDirection ?? '';
    this.cameraRotation = form.cameraRotation ?? '';
    this.lockerType = form.lockerType ?? '';
    this.countryCode = form.countryCode ?? DEFAULT_COUNTRY_CODE;
    this.internalUserSingular = form.internalUserSingular ?? '';
    this.internalUserPlural = form.internalUserPlural ?? '';
    this.externalUserSingular = form.externalUserSingular ?? '';
    this.externalUserPlural = form.externalUserPlural ?? '';
    this.scanQrCodeHeading = form.scanQrCodeHeading ?? '';
    this.scanQrCodeSubheading = form.scanQrCodeSubheading ?? '';
    this.externalLoginPhoneLoginHeading = form.externalLoginPhoneLoginHeading ?? '';
    this.externalLoginPhoneLoginSubheading = form.externalLoginPhoneLoginSubheading ?? '';
    this.collectionCodeSubHeading = form.collectionCodeSubHeading ?? '';
    this.noDoorsAvailableSubheading = form.noDoorsAvailableSubheading ?? '';
    this.csPhoneInternal = form.csPhoneInternal ?? '';
    this.csPhoneExternal = form.csPhoneExternal ?? '';
    this.groundfloorPortalAddress = form.groundfloorPortalAddress ?? '';
  }

  /**
   * `defaults` is the kiosk-wide default settings: the settings endpoint returns defaults merged into
   * the payload, so a string equal to its default is treated as not customised (blank in the form).
   */
  static FromDto(dto: KioskSettingsDto, defaults?: KioskSettingsDto): KioskSettingsForm {
    const internalBtn = findButton(dto.homeUi?.buttonRows, INTERNAL_USER_ACTION);
    const externalBtn = findButton(dto.homeUi?.buttonRows, EXTERNAL_USER_ACTION);
    return {
      name: dto.name ?? '',
      mode: dto.mode?.type ?? '',
      showInternalUserButton: internalBtn ? 'yes' : 'no',
      internalUserButtonText: internalBtn?.label?.[LABEL_LOCALE] ?? '',
      showExternalUserButton: externalBtn ? 'yes' : 'no',
      externalUserButtonText: externalBtn?.label?.[LABEL_LOCALE] ?? '',
      externalFlow: dto.externalFlow ?? '',
      scannerMode: dto.hardwareConfiguration?.scannerMode ?? KnownKioskHardwareConfigurationDtoScannerMode.Cdc,
      cameraEnabled: dto.hardwareConfiguration?.cameraEnabled ?? true,
      cameraDriver: dto.hardwareConfiguration?.cameraDriver ?? KnownKioskHardwareConfigurationDtoCameraDriver.Default,
      cameraDirection:
        dto.hardwareConfiguration?.cameraDirection ?? KnownKioskHardwareConfigurationDtoCameraDirection.External,
      cameraRotation:
        dto.hardwareConfiguration?.cameraRotation ?? KnownKioskHardwareConfigurationDtoCameraRotation.None,
      lockerType: dto.hardwareConfiguration?.lockerType ?? '',
      countryCode: dto.location?.countryCode ?? DEFAULT_COUNTRY_CODE,
      ...mapStrings(STRING_KEYS, (key) => customisedString(dto.strings?.[key], defaults?.strings?.[key]))
    };
  }

  /**
   * Non-blank strings are always sent, even when they happen to equal the default — typing a value
   * is an explicit choice. Strings left blank (cleared) are sent as the kiosk-wide default string,
   * or omitted when no default is known.
   */
  static ToDto(
    output: KioskSettingsFormOutput,
    existingDto: KioskSettingsDto | undefined,
    defaults?: KioskSettingsDto
  ): KioskSettingsDto {
    const updatedRows = buildButtonRows(existingDto?.homeUi?.buttonRows ?? [], output);
    const dto: KioskSettingsDto = {
      name: output.name || undefined,
      mode: output.mode ? { type: output.mode } : undefined,
      homeUi: { buttonRows: updatedRows },
      // Omitted when blank so the API keeps the current value; `null` would be rejected.
      externalFlow: output.externalFlow || undefined,
      hardwareConfiguration: {
        scannerMode: output.scannerMode || undefined,
        cameraEnabled: output.cameraEnabled,
        cameraDriver: output.cameraDriver || undefined,
        cameraDirection: output.cameraDirection || undefined,
        cameraRotation: output.cameraRotation || undefined,
        lockerType: output.lockerType || undefined
      },
      location: { countryCode: output.countryCode || DEFAULT_COUNTRY_CODE },
      strings: mapStrings(STRING_KEYS, (key) => output[key] || defaults?.strings?.[key] || undefined)
    };
    return dto;
  }

  /**
   * A client kiosk inherits everything except its name, mode and home screen buttons (and branding, which the
   * kiosk theme form edits) from its manager, so those fields are read from the manager's settings.
   */
  static FromClientDto(
    clientDto: KioskSettingsDto,
    managerDto: KioskSettingsDto | undefined,
    defaults?: KioskSettingsDto
  ): KioskSettingsForm {
    const client = KioskSettingsFormOutput.FromDto(clientDto, defaults);
    return {
      ...KioskSettingsFormOutput.FromDto(managerDto ?? {}, defaults),
      name: client.name,
      mode: client.mode,
      showInternalUserButton: client.showInternalUserButton,
      internalUserButtonText: client.internalUserButtonText,
      showExternalUserButton: client.showExternalUserButton,
      externalUserButtonText: client.externalUserButtonText
    };
  }

  /** Only the client-owned settings are sent; inherited fields are never written to the client kiosk. */
  static ToClientDto(output: KioskSettingsFormOutput, existingDto: KioskSettingsDto | undefined): KioskSettingsDto {
    return {
      name: output.name || undefined,
      mode: output.mode ? { type: output.mode } : undefined,
      homeUi: { buttonRows: buildButtonRows(existingDto?.homeUi?.buttonRows ?? [], output) }
    };
  }
}

function findButton(buttonRows: KioskHomeButtonDto[][] | undefined, action: string): KioskHomeButtonDto | undefined {
  if (!buttonRows) return undefined;
  for (const row of buttonRows) {
    const btn = row.find((b) => b.action === action);
    if (btn) return btn;
  }
  return undefined;
}

function buildButtonRows(currentRows: KioskHomeButtonDto[][], output: KioskSettingsFormOutput): KioskHomeButtonDto[][] {
  const rows = currentRows.map((row) => [...row]);
  setButton(rows, INTERNAL_USER_ACTION, output.showInternalUserButton === 'yes', output.internalUserButtonText);
  setButton(rows, EXTERNAL_USER_ACTION, output.showExternalUserButton === 'yes', output.externalUserButtonText);
  return rows.filter((row) => row.length > 0);
}

function setButton(rows: KioskHomeButtonDto[][], action: string, show: boolean, labelText: string) {
  for (const row of rows) {
    const idx = row.findIndex((b) => b.action === action);
    if (idx !== -1) {
      if (!show) {
        row.splice(idx, 1);
      } else {
        row[idx] = { ...row[idx], label: { ...row[idx].label, [LABEL_LOCALE]: labelText } };
      }
      return;
    }
  }
  if (show) {
    if (rows.length === 0) rows.push([]);
    rows[0].push({ action, label: { [LABEL_LOCALE]: labelText }, style: BUTTON_DEFAULT_STYLE });
  }
}
